import type { CameraPosition, CameraShape } from "./types";

const BORDER_RADIUS = 20;

export const drawFrame = (
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number,
  staticCanvas: HTMLCanvasElement | null,
  newElementCanvas: HTMLCanvasElement | null,
  interactiveCanvas: HTMLCanvasElement | null,
  svgDrawFn:
    | ((ctx: CanvasRenderingContext2D, w: number, h: number) => void)
    | null,
  cursorDrawFn:
    | ((ctx: CanvasRenderingContext2D, w: number, h: number) => void)
    | null,
  cameraSource: HTMLVideoElement | HTMLCanvasElement | null,
  cameraPosition: CameraPosition,
  cameraShape: CameraShape,
  cameraMirrored: boolean,
  cameraSize: number,
): void => {
  ctx.clearRect(0, 0, canvasWidth, canvasHeight);

  if (staticCanvas) {
    ctx.drawImage(staticCanvas, 0, 0, canvasWidth, canvasHeight);
  }

  if (newElementCanvas) {
    ctx.drawImage(newElementCanvas, 0, 0, canvasWidth, canvasHeight);
  }

  if (interactiveCanvas) {
    ctx.drawImage(interactiveCanvas, 0, 0, canvasWidth, canvasHeight);
  }

  if (svgDrawFn) {
    svgDrawFn(ctx, canvasWidth, canvasHeight);
  }

  if (cursorDrawFn) {
    cursorDrawFn(ctx, canvasWidth, canvasHeight);
  }

  const cameraReady =
    cameraSource instanceof HTMLVideoElement
      ? cameraSource.readyState >= 2
      : cameraSource !== null && cameraSource.width > 0;

  if (cameraSource && cameraReady) {
    const cx =
      cameraPosition.x >= 0 ? cameraPosition.x : canvasWidth - cameraSize - 20;
    const cy =
      cameraPosition.y >= 0 ? cameraPosition.y : canvasHeight - cameraSize - 20;

    ctx.save();

    if (cameraShape === "circle") {
      ctx.beginPath();
      ctx.arc(
        cx + cameraSize / 2,
        cy + cameraSize / 2,
        cameraSize / 2,
        0,
        Math.PI * 2,
      );
      ctx.clip();
    } else {
      ctx.beginPath();
      ctx.roundRect!(cx, cy, cameraSize, cameraSize, BORDER_RADIUS);
      ctx.clip();
    }

    if (cameraMirrored) {
      ctx.translate(cx + cameraSize, cy);
      ctx.scale(-1, 1);
    }

    const sourceWidth =
      cameraSource instanceof HTMLVideoElement
        ? cameraSource.videoWidth
        : cameraSource.width;
    const sourceHeight =
      cameraSource instanceof HTMLVideoElement
        ? cameraSource.videoHeight
        : cameraSource.height;

    const videoAspect = sourceWidth / sourceHeight;
    let sx = 0;
    let sy = 0;
    let sw = sourceWidth;
    let sh = sourceHeight;

    if (videoAspect > 1) {
      sw = sourceHeight;
      sx = (sourceWidth - sw) / 2;
    } else if (videoAspect < 1) {
      sh = sourceWidth;
      sy = (sourceHeight - sh) / 2;
    }

    const drawX = cameraMirrored ? 0 : cx;
    const drawY = cameraMirrored ? 0 : cy;

    ctx.drawImage(
      cameraSource,
      sx,
      sy,
      sw,
      sh,
      drawX,
      drawY,
      cameraSize,
      cameraSize,
    );
    ctx.restore();

    ctx.save();
    ctx.strokeStyle = "rgba(255, 255, 255, 0.6)";
    ctx.lineWidth = 2;
    if (cameraShape === "circle") {
      ctx.beginPath();
      ctx.arc(
        cx + cameraSize / 2,
        cy + cameraSize / 2,
        cameraSize / 2,
        0,
        Math.PI * 2,
      );
      ctx.stroke();
    } else {
      ctx.beginPath();
      ctx.roundRect!(cx, cy, cameraSize, cameraSize, BORDER_RADIUS);
      ctx.stroke();
    }
    ctx.restore();
  }

};

export const createCompositorCanvas = (
  width: number,
  height: number,
): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } => {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d")!;
  return { canvas, ctx };
};
